import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ArrowRight, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Memory AI Chatbox — glassmorphism dark, conversational with Gemini 2.5 Flash.
 * Props:
 *  - language: "id" | "en"
 *  - compact: if true, smaller height for landing section
 *  - onReadyCta: callback when CTA pressed
 */
export default function MemoryAIChat({ language = "id", compact = true, onReadyCta }) {
  const navigate = useNavigate();
  const [session, setSession] = useState(null); // { session_id, intro, message, ... }
  const [history, setHistory] = useState([]); // {role, text}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [canShowCta, setCanShowCta] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [ctaLabel, setCtaLabel] = useState(language === "id" ? "Rangkai Memory Bouquet" : "Create My Memory Bouquet");
  const scrollerRef = useRef(null);

  // Start session on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/memory/chat/start`, { language });
        if (cancelled) return;
        setSession(data);
        setHistory([{ role: "ai", text: data.message }]);
        setCtaLabel(data.cta_label || ctaLabel);
      } catch (e) {
        console.error("Memory chat start failed", e);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [language]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [history, sending]);

  const send = async () => {
    if (!session || !input.trim() || sending) return;
    const text = input.trim();
    setInput("");
    setHistory((h) => [...h, { role: "user", text }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/memory/chat/${session.session_id}/message`, { message: text });
      setHistory((h) => [...h, { role: "ai", text: data.message }]);
      setCanShowCta(!!data.can_show_cta);
      setIsReady(!!data.is_ready);
      if (data.cta_label) setCtaLabel(data.cta_label);
    } catch (e) {
      console.error("Memory chat send failed", e);
      setHistory((h) => [...h, { role: "ai", text: language === "id"
        ? "Maaf, koneksiku terputus sejenak. Coba lagi ya."
        : "Sorry, my connection slipped for a moment. Try again." }]);
    } finally {
      setSending(false);
    }
  };

  const handleCta = () => {
    // Pass conversation context to the flow wizard via sessionStorage
    if (history.length > 1) {
      const storyLines = history
        .filter(m => m.role === "user")
        .map(m => m.text)
        .join(" ");
      if (storyLines.trim().length > 0) {
        sessionStorage.setItem("mb_chat_story", storyLines.trim());
      }
    }
    if (onReadyCta) onReadyCta(session);
    else navigate("/memory-bouquet/create");
  };

  return (
    <div
      className={`glass-dark-strong rounded-3xl w-full max-w-2xl mx-auto flex flex-col ${compact ? "h-[60vh] max-h-[480px]" : "h-[72vh] max-h-[640px]"}`}
      data-testid="memory-ai-chat"
    >
      <div className="px-6 pt-5 pb-3 flex items-center justify-between border-b border-white/5">
        <div className="flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-seikat-lime animate-pulse" />
          <span className="eyebrow-immersive">Memory Curator</span>
        </div>
        <span className="text-[10px] tracking-widest uppercase text-seikat-bone-muted">AI · Gemini</span>
      </div>

      <div ref={scrollerRef} className="flex-1 overflow-y-auto no-scrollbar px-6 py-5 space-y-4">
        <AnimatePresence initial={false}>
          {history.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              data-testid={`chat-bubble-${m.role}-${i}`}
            >
              <div
                className={`max-w-[82%] rounded-2xl px-4 py-3 font-body text-[0.95rem] leading-relaxed ${
                  m.role === "user"
                    ? "bg-white/10 text-seikat-bone rounded-tr-sm"
                    : "bg-seikat-pink/15 border border-seikat-pink/25 text-seikat-bone rounded-tl-sm"
                }`}
              >
                {m.text}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {sending && (
          <div className="flex justify-start" data-testid="chat-typing">
            <div className="bg-seikat-pink/10 border border-seikat-pink/20 rounded-2xl rounded-tl-sm px-4 py-3">
              <span className="typing-dot" />
              <span className="typing-dot" />
              <span className="typing-dot" />
            </div>
          </div>
        )}
      </div>

      {/* CTA when ready */}
      <AnimatePresence>
        {canShowCta && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="px-6 py-3 border-t border-white/5"
          >
            <button
              onClick={handleCta}
              data-testid="memory-chat-cta"
              data-magnet
              className="btn-pill btn-pill--solid w-full"
            >
              {ctaLabel}
              <ArrowRight size={14} className="ml-1" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Input */}
      <div className="px-4 pb-4 pt-2 border-t border-white/5">
        <div className="flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-4 py-2.5 focus-within:border-seikat-pink/60 transition-colors">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={language === "id" ? "Ceritakan sedikit..." : "Tell us a little..."}
            data-testid="memory-chat-input"
            className="flex-1 bg-transparent outline-none text-seikat-bone placeholder:text-seikat-bone-muted/70 font-body text-[0.95rem]"
            disabled={sending || isReady}
          />
          <button
            onClick={send}
            disabled={!input.trim() || sending || isReady}
            data-testid="memory-chat-send"
            data-magnet
            className="text-seikat-bone-muted hover:text-seikat-pink transition-colors disabled:opacity-40"
            aria-label="Send"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
